// System announcements (Admin Review → Announcements, 2026-10-27): the yellow-orange bar
// every signed-in user sees until they press Acknowledge or the message runs out. Layout
// puts it at the top of every page, the Dashboard right above Today's boost, and the
// client portal above its pages. Staff and the portal each keep ONE shared list, so
// acknowledging on one page clears it everywhere. Backend: backend/src/announcements.js.
import { useState, useEffect } from "react";
import { Megaphone, AlertTriangle, Check } from "lucide-react";
import { getActiveAnnouncements, ackAnnouncement, getPortalAnnouncements, ackPortalAnnouncement } from "../api";

const SOURCES = {
  staff:  { load: getActiveAnnouncements, ack: ackAnnouncement, poll: 60000 },
  // A portal call renews the client's sign-in, so the portal never polls in the
  // background (an open tab would never sign out). It checks on load and on every page change.
  portal: { load: getPortalAnnouncements, ack: ackPortalAnnouncement, poll: 0 },
};

const stores = {};
const store = (kind) => (stores[kind] ||= { list: [], at: 0, subs: new Set(), inflight: null, acked: new Set() });
const publish = (s, list) => { s.list = list; s.subs.forEach((f) => f(list)); };
function refresh(kind, maxAgeMs = 0) {
  const s = store(kind);
  if (s.inflight || Date.now() - s.at < maxAgeMs) return;
  s.inflight = SOURCES[kind].load()
    .then((r) => { s.at = Date.now(); publish(s, (Array.isArray(r.data) ? r.data : []).filter((a) => !s.acked.has(a.id))); })
    .catch(() => {})
    .finally(() => { s.inflight = null; });
}

// One bar. Also used as the live preview on the Admin Review form.
export function AnnouncementCard({ kind, message, onAck, busy, error }) {
  const alert = kind === "alert";
  const Icon = alert ? AlertTriangle : Megaphone;
  return (
    <div role={alert ? "alert" : "status"} style={{
      display:"flex", alignItems:"center", gap:14, flexWrap:"wrap", padding:"12px 14px 12px 16px", borderRadius:12,
      background:"linear-gradient(90deg, #fcd34d 0%, #fbbf24 45%, #fb923c 100%)", color:"#1c1304",
      border:"1px solid #f59e0b", boxShadow:"0 4px 18px rgba(245,158,11,0.22)" }}>
      <Icon size={20} style={{ flexShrink:0 }} />
      <div style={{ flex:"1 1 260px", minWidth:0 }}>
        <div style={{ fontSize:11, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", opacity:0.72 }}>
          {alert ? "System alert" : "Status update"}
        </div>
        <div style={{ fontSize:14.5, fontWeight:600, lineHeight:1.5, marginTop:2, whiteSpace:"pre-wrap", overflowWrap:"anywhere" }}>
          {message}
        </div>
        {error && <div style={{ fontSize:12, fontWeight:700, color:"#7f1d1d", marginTop:4 }}>{error}</div>}
      </div>
      <button type="button" onClick={onAck} disabled={busy} style={{
        display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:8, border:"none",
        background:"#1c1304", color:"#fde68a", fontFamily:"inherit", fontSize:13, fontWeight:700, flexShrink:0,
        cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
        <Check size={14} /> {busy ? "Saving…" : "Acknowledge"}
      </button>
    </div>
  );
}

// kind: "staff" | "portal". refreshKey: the current path, so a page change checks for new messages.
export default function AnnouncementBar({ kind = "staff", refreshKey, style }) {
  const s = store(kind);
  const [list, setList] = useState(s.list);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => { s.subs.add(setList); setList(s.list); return () => { s.subs.delete(setList); }; }, [s]);
  useEffect(() => { refresh(kind, 15000); }, [kind, refreshKey]);
  useEffect(() => {
    const ms = SOURCES[kind].poll;
    if (!ms) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") refresh(kind); }, ms);
    const onVis = () => { if (document.visibilityState === "visible") refresh(kind, 15000); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [kind]);
  // Drop a message the moment its time runs out, even if nothing else re-renders the page.
  useEffect(() => {
    const next = Math.min(...list.map((a) => new Date(a.ends_at).getTime()));
    if (!Number.isFinite(next)) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), Math.min(Math.max(0, next - Date.now()) + 500, 3600000));
    return () => clearTimeout(t);
  }, [list, tick]);

  const acknowledge = async (a) => {
    setBusy(a.id); setErr(null);
    try {
      await SOURCES[kind].ack(a.id);
      s.acked.add(a.id);
      publish(s, s.list.filter((x) => x.id !== a.id));
    } catch (e) {
      if (e.response?.status === 404) { s.acked.add(a.id); publish(s, s.list.filter((x) => x.id !== a.id)); }
      else setErr({ id: a.id, msg: e.response?.data?.error || "Couldn't save that. Try again." });
    } finally { setBusy(null); }
  };

  const now = Date.now();
  const visible = list.filter((a) => new Date(a.ends_at).getTime() > now);
  if (!visible.length) return null;
  return (
    <div aria-label="System announcements" style={{ display:"flex", flexDirection:"column", gap:8, ...style }}>
      {visible.map((a) => (
        <AnnouncementCard key={a.id} kind={a.kind} message={a.message} busy={busy === a.id}
          error={err?.id === a.id ? err.msg : null} onAck={() => acknowledge(a)} />
      ))}
    </div>
  );
}
